
    import { mapGetters, mapState } from 'vuex'
    import { Component, Inject, Vue } from 'vue-property-decorator'
    import Layout from '@/app/common/components/layout/layout.vue'
    import DeleteConfirmationDialog from '@/app/my-uploaded-documents/components/delete-confirmation-dialog.vue'
    import { GetMyUploadedDocumentsCommand } from '@/app/my-uploaded-documents/commands/get-my-uploaded-documents.command'
    import { ViewMyUploadedDocumentCommand } from '@/app/my-uploaded-documents/commands/view-my-uploaded-document.command'
    import { DeleteMyUploadedDocumentCommand } from '@/app/my-uploaded-documents/commands/delete-my-uploaded-document.command'
    import { MyUploadedDocument } from '@/app/my-uploaded-documents/entities/my-uploaded-document.entity'
    import { NotificationDispatcher } from '@/app/common/services/notification-dispatcher.service'
    import DocumentPreview from '@/app/common/components/document-preview.vue'
    import { License } from '@/app/license/entities/license.entity'

    @Component({
        components: { Layout, DocumentPreview, DeleteConfirmationDialog },
        metaInfo() {
            return {
                title: this.$t('anchors.my-uploaded-documents') as string
            }
        },
        computed: {
            ...mapState('myUploadedDocuments', ['documents', 'hasLoaded']),
            ...mapGetters('myUploadedDocuments', ['sortedDocuments']),
            ...mapState('license', ['license'])
        }
    })
    export default class MyUploadedDocumentsPage extends Vue {
        hasLoaded!: boolean
        documents!: MyUploadedDocument[]
        sortedDocuments!: MyUploadedDocument[]
        license!: License

        showPdfModal!: boolean
        pdfAsFile!: File | null
        loadingPdf!: boolean
        pdfError!: string | null
        selectedDocument!: MyUploadedDocument | null
        loadingDocumentId!: string | null
        showDeleteDialog!: boolean
        documentToDelete!: MyUploadedDocument | null
        deletingDocument!: boolean
        deletingDocumentId!: string | null

        @Inject() getMyDocumentsCommand!: GetMyUploadedDocumentsCommand
        @Inject() viewMyUploadedDocumentCommand!: ViewMyUploadedDocumentCommand
        @Inject() deleteMyUploadedDocumentCommand!: DeleteMyUploadedDocumentCommand
        @Inject() notificationDispatcher!: NotificationDispatcher

        data() {
            return {
                showPdfModal: false,
                pdfAsFile: null as File | null,
                loadingPdf: false,
                pdfError: null as string | null,
                selectedDocument: null as MyUploadedDocument | null,
                loadingDocumentId: null as string | null,
                showDeleteDialog: false,
                documentToDelete: null as MyUploadedDocument | null,
                deletingDocument: false,
                deletingDocumentId: null as string | null
            }
        }

        get headers() {
            return [
                {
                    text: this.$t('page.my-uploaded-documents.document-table.name').toString(),
                    value: 'documentType',
                    sortable: true,
                    width: '25%'
                },
                {
                    text: this.$t('page.my-uploaded-documents.document-table.description').toString(),
                    value: 'description',
                    sortable: true,
                    width: '30%'
                },
                {
                    text: this.$t('page.my-uploaded-documents.document-table.remarks').toString(),
                    value: 'remarks',
                    sortable: false,
                    width: '25%'
                },
                {
                    text: this.$t('page.my-uploaded-documents.document-table.date-uploaded').toString(),
                    value: 'uploadedAt',
                    sortable: true,
                    width: '15%'
                },
                {
                    text: this.$t('page.my-uploaded-documents.document-table.actions').toString(),
                    value: 'actions',
                    sortable: false,
                    align: 'start',
                    width: '5%'
                }
            ]
        }

        get canDeleteDocuments() {
            return !!(this.license && this.license.features && this.license.features.canDeleteUploadedDocuments)
        }

        async mounted() {
            if (!this.license || !this.license.features || !this.license.features.canViewUploadedDocuments) {
                await this.$router.push({ name: 'dashboard' })
                return
            }

            await this.getMyDocumentsCommand.run()

            if (this.hasLoaded && this.documents.length === 0) {
                await this.$router.push({ name: 'dashboard' })
            }
        }

        async viewDocument(document: MyUploadedDocument) {
            this.selectedDocument = document
            this.loadingDocumentId = document.id
            this.loadingPdf = true
            this.pdfError = null

            try {
                const pdfData = await this.viewMyUploadedDocumentCommand.run(document.id)
                this.pdfAsFile = new File([pdfData], document.name, { type: 'application/pdf' })
                this.showPdfModal = true
            } catch (err) {
                this.pdfError = this.$t('page.my-uploaded-documents.document-table.failed-to-load-document').toString()
                await this.notificationDispatcher.error(
                    (err as Error)?.message ||
                        this.$t('page.my-uploaded-documents.document-table.failed-to-load-document').toString()
                )
            } finally {
                this.loadingPdf = false
                this.loadingDocumentId = null
            }
        }

        deleteDocument(document: MyUploadedDocument) {
            this.documentToDelete = document
            this.showDeleteDialog = true
        }

        cancelDelete() {
            this.showDeleteDialog = false
            this.documentToDelete = null
        }

        async confirmDelete() {
            if (!this.documentToDelete) {
                return
            }

            this.deletingDocument = true
            this.deletingDocumentId = this.documentToDelete.id

            try {
                await this.deleteMyUploadedDocumentCommand.run(this.documentToDelete.id)

                await this.getMyDocumentsCommand.run()

                await this.notificationDispatcher.success(
                    this.$t('page.my-uploaded-documents.delete-dialog.success').toString()
                )

                this.showDeleteDialog = false
                this.documentToDelete = null
            } catch (err) {
                const errorMessage = (err as Error)?.message || ''
                const isNotAllowedError = errorMessage.includes('DOCUMENT_DELETE_NOT_ALLOWED')

                await this.notificationDispatcher.error(
                    isNotAllowedError
                        ? this.$t('page.my-uploaded-documents.delete-dialog.not-allowed').toString()
                        : this.$t('page.my-uploaded-documents.delete-dialog.error').toString()
                )
            } finally {
                this.deletingDocument = false
                this.deletingDocumentId = null
            }
        }
    }
